import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const blogSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Fluffy Pets Pet Care Blog',
    url: `${SITE_URL}/blog`,
    description:
      'Expert pet care tips, grooming advice, health guides, training methods, and nutrition information for pet owners.',
    publisher: {
      '@type': 'Organization',
      name: 'Fluffy Pets',
      url: SITE_URL,
      logo: 'https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/7319ff1316bfcbedde225c0cf8d58231.jpeg',
    },
    blogPost: [
      {
        '@type': 'BlogPosting',
        headline: '10 Essential Grooming Tips for Your Dog',
        description:
          'Learn the best practices to keep your furry friend looking and feeling their best with these professional grooming tips.',
        author: { '@type': 'Person', name: 'Sarah Johnson' },
        datePublished: '2024-03-15',
        keywords: 'dog grooming, pet care, grooming tips',
      },
      {
        '@type': 'BlogPosting',
        headline: "Understanding Your Cat's Behavior",
        description:
          "Decode your feline friend's mysterious behaviors and learn what they're trying to tell you through their actions.",
        author: { '@type': 'Person', name: 'Dr. Emily Chen' },
        datePublished: '2024-03-12',
        keywords: 'cat behavior, cat care, pet behavior',
      },
      {
        '@type': 'BlogPosting',
        headline: 'Nutrition Guide: What to Feed Your Pet',
        description:
          "A comprehensive guide to choosing the right food for your pet's age, breed, and health needs.",
        author: { '@type': 'Person', name: 'Mark Thompson' },
        datePublished: '2024-03-10',
        keywords: 'pet nutrition, pet food, healthy diet for pets',
      },
    ],
  },
];

// ... existing code ...

export default function BlogPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Pet Care', 'Grooming Tips', 'Health', 'Training', 'Nutrition'];

  const blogPosts = [
    {
      id: 1,
      title: '10 Essential Grooming Tips for Your Dog',
      excerpt: 'Learn the best practices to keep your furry friend looking and feeling their best with these professional grooming tips.',
      category: 'Grooming Tips',
      image: 'https://public.readdy.ai/ai/img_res/8aa7f5aeab10c8936d87d8431f585923.jpg',
      date: 'March 15, 2024',
      readTime: '5 min read',
      author: 'Sarah Johnson'
    },
    {
      id: 2,
      title: 'Understanding Your Cat\'s Behavior',
      excerpt: 'Decode your feline friend\'s mysterious behaviors and learn what they\'re trying to tell you through their actions.',
      category: 'Pet Care',
      image: 'https://readdy.ai/api/search-image?query=curious%20orange%20tabby%20cat%20displaying%20various%20behaviors%20sitting%20playing%20stretching%20in%20cozy%20home%20environment%20with%20toys%20and%20cat%20furniture%20soft%20natural%20lighting&width=800&height=500&seq=blog2&orientation=landscape',
      date: 'March 12, 2024',
      readTime: '7 min read',
      author: 'Dr. Emily Chen'
    },
    {
      id: 3,
      title: 'Nutrition Guide: What to Feed Your Pet',
      excerpt: 'A comprehensive guide to choosing the right food for your pet\'s age, breed, and health needs.',
      category: 'Nutrition',
      image: 'https://readdy.ai/api/search-image?query=healthy%20pet%20food%20bowls%20with%20fresh%20ingredients%20vegetables%20meat%20kibble%20arranged%20beautifully%20on%20clean%20white%20surface%20with%20happy%20dog%20and%20cat%20in%20background%20bright%20kitchen%20setting&width=800&height=500&seq=blog3&orientation=landscape',
      date: 'March 10, 2024',
      readTime: '6 min read',
      author: 'Mark Thompson'
    },
    {
      id: 4,
      title: 'Preparing Your Pet for Boarding',
      excerpt: 'Make your pet\'s boarding experience stress-free with these helpful preparation tips and tricks.',
      category: 'Pet Care',
      image: 'https://readdy.ai/api/search-image?query=comfortable%20pet%20boarding%20facility%20with%20cozy%20beds%20toys%20and%20happy%20dogs%20and%20cats%20relaxing%20in%20spacious%20clean%20modern%20environment%20with%20caring%20staff%20member&width=800&height=500&seq=blog4&orientation=landscape',
      date: 'March 8, 2024',
      readTime: '4 min read',
      author: 'Sarah Johnson'
    },
    {
      id: 5,
      title: 'Common Health Issues in Senior Pets',
      excerpt: 'Recognize the signs of aging in your pet and learn how to provide the best care for their golden years.',
      category: 'Health',
      image: 'https://readdy.ai/api/search-image?query=senior%20gray%20dog%20and%20elderly%20cat%20being%20examined%20by%20veterinarian%20in%20bright%20modern%20clinic%20with%20medical%20equipment%20caring%20atmosphere%20professional%20healthcare%20setting&width=800&height=500&seq=blog5&orientation=landscape',
      date: 'March 5, 2024',
      readTime: '8 min read',
      author: 'Dr. Emily Chen'
    },
    {
      id: 6,
      title: 'Positive Reinforcement Training Methods',
      excerpt: 'Discover effective training techniques that strengthen your bond with your pet while teaching good behavior.',
      category: 'Training',
      image: 'https://readdy.ai/api/search-image?query=happy%20dog%20learning%20tricks%20with%20trainer%20using%20positive%20reinforcement%20treats%20and%20praise%20in%20outdoor%20training%20area%20sunny%20day%20encouraging%20atmosphere&width=800&height=500&seq=blog6&orientation=landscape',
      date: 'March 3, 2024',
      readTime: '6 min read',
      author: 'Mark Thompson'
    }
  ];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E6E0F5] via-[#F0EBF8] to-[#D4C5F0]">
      <SEOHead
        title="Pet Care Blog | Expert Tips on Grooming, Health &amp; Training - Fluffy Pets"
        description="Read expert pet care articles covering grooming tips, pet health guides, training methods, and nutrition advice. Stay informed and give your pets the best care possible."
        keywords="pet care blog, dog grooming tips, cat behavior, pet nutrition, pet training, senior pet health"
        canonical={`${SITE_URL}/blog`}
        schema={blogSchema}
        ogType="website"
      />
      {/* Navbar */}
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/7319ff1316bfcbedde225c0cf8d58231.jpeg" 
                alt="Fluffy Pets Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-[#9B4C9A] hover:text-[#FF9BC8] transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-semibold">Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent" style={{ fontFamily: 'Pacifico, cursive' }}>
            Pet Care Blog
          </h1>
          <p className="text-xl text-[#4A3B5C] max-w-3xl mx-auto leading-relaxed">
            Expert advice, tips, and insights to help you provide the best care for your beloved pets
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white shadow-lg'
                    : 'bg-white text-[#9B4C9A] hover:shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-105 cursor-pointer"
              >
                <div className="w-full h-64 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-4 py-1 bg-gradient-to-r from-[#9B4C9A]/10 to-[#FF9BC8]/10 text-[#9B4C9A] rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                    <span className="text-[#8B7BA8] text-sm">{post.readTime}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#4A3B5C] mb-3 leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-[#8B7BA8] leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-[#E6E0F5]">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-white text-sm"></i>
                      </div>
                      <span className="text-sm text-[#4A3B5C] font-semibold">{post.author}</span>
                    </div>
                    <span className="text-sm text-[#8B7BA8]">{post.date}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] rounded-3xl p-12 text-center text-white shadow-2xl">
          <i className="ri-mail-line text-6xl mb-6"></i>
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Pacifico, cursive' }}>
            Never Miss a Post
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Subscribe to our newsletter and get the latest pet care tips delivered to your inbox
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-white text-[#9B4C9A] px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            Subscribe Now
          </button>
        </div>
      </section>
    </div>
  );
}
